/**
 * useHandGestures — Phase 5c orchestrator hook (Task 12.8, GREEN).
 *
 * Thin composition layer that owns the camera lifecycle, the gesture pipeline
 * (smoother → debouncer → cooldown → dispatch), action dispatch, and the
 * enable/disable lifecycle. Composes useCameraStream + useWorkerBridge
 * conceptually but implements their concerns inline for tight lifecycle control.
 *
 * _Requirements: 1.4, 1.6, 2.2, 2.5, 2.6, 3.1, 3.2, 3.5, 3.6, 8.4, 12.1, 12.2_
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  LandmarkSmoother,
  GestureDebouncer,
  GestureCooldown,
  SwipeDetector,
} from '../utils/gesture-smoothing';
import { DEFAULT_GESTURE_CONFIG, validateGestureConfig } from '../utils/gesture-config';
import type { GestureConfig as GestureConfigFull } from '../types/hand-gestures';
import type {
  GestureAction,
  GestureWorkerResult,
  GestureWorkerResponse,
  Landmark,
} from '../types/hand-gestures';

// ---------------------------------------------------------------------------
// WorkerLike interface (matches useWorkerBridge's export)
// ---------------------------------------------------------------------------

export interface WorkerLike {
  postMessage: (msg: unknown, transfer?: Transferable[]) => void;
  terminate: () => void;
  onmessage: ((e: { data: GestureWorkerResponse }) => void) | null;
  onerror: ((e: unknown) => void) | null;
}

// ---------------------------------------------------------------------------
// Options & return types
// ---------------------------------------------------------------------------

export interface UseHandGesturesOptions {
  onAction?: (action: GestureAction) => void;
  canGoNext?: boolean;
  canGoPrev?: boolean;
  config?: GestureConfigFull;
  createWorker?: () => WorkerLike;
}

export interface UseHandGesturesReturn {
  isActive: boolean;
  isLoading: boolean;
  currentGesture: string | null;
  confidence: number;
  error: string | null;
  stream: MediaStream | null;
  enable: () => Promise<void>;
  disable: () => void;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_DEBOUNCE_FRAMES = 3;
const DEFAULT_SMOOTHING_FACTOR = 0.15;
const DEFAULT_PROCESSING_FPS = 30;
const DEFAULT_COOLDOWN_SWIPE_MS = 800;
const DEFAULT_COOLDOWN_STATIC_MS = 1000;
const DEFAULT_PALM_HOLD_DURATION_MS = 1000;
const DEFAULT_PALM_VELOCITY_THRESHOLD = 0.03;

const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
};

/** Default worker factory: creates the real MediaPipe gesture worker. */
function defaultCreateWorker(): WorkerLike {
  return new Worker(
    new URL('../workers/mediapipe.worker.ts', import.meta.url),
    { type: 'classic' }
  ) as unknown as WorkerLike;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useHandGestures(options: UseHandGesturesOptions = {}): UseHandGesturesReturn {
  const {
    onAction,
    canGoNext = false,
    canGoPrev = false,
    config: userConfig,
    createWorker,
  } = options;

  // --- React state exposed to the consumer ----------------------------------
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentGesture, setCurrentGesture] = useState<string | null>(null);
  const [confidence, setConfidence] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // --- Refs for latest props (avoid stale closures in rAF / callbacks) ------
  const onActionRef = useRef(onAction);
  const canGoNextRef = useRef(canGoNext);
  const canGoPrevRef = useRef(canGoPrev);
  const createWorkerRef = useRef(createWorker);
  onActionRef.current = onAction;
  canGoNextRef.current = canGoNext;
  canGoPrevRef.current = canGoPrev;
  createWorkerRef.current = createWorker;

  // --- Resolve gesture config -----------------------------------------------
  const resolvedConfig: GestureConfigFull = userConfig ?? {
    mappings: DEFAULT_GESTURE_CONFIG.mappings,
  };
  const validationResult = validateGestureConfig({ mappings: resolvedConfig.mappings });
  const validMappings = validationResult.valid
    ? resolvedConfig.mappings
    : validationResult.validMappings;

  // Build the gesture→action lookup from config (V4-2).
  const actionByGestureRef = useRef<Map<string, GestureAction>>(new Map());
  actionByGestureRef.current = new Map(validMappings.map((m) => [m.gesture, m.action]));

  // Config values with defaults.
  const debounceFrames = resolvedConfig.debounceFrames ?? DEFAULT_DEBOUNCE_FRAMES;
  const smoothingFactor = resolvedConfig.smoothingFactor ?? DEFAULT_SMOOTHING_FACTOR;
  const processingFps = resolvedConfig.processingFps ?? DEFAULT_PROCESSING_FPS;
  const cooldownSwipeMs = resolvedConfig.cooldowns?.swipe ?? DEFAULT_COOLDOWN_SWIPE_MS;
  const cooldownStaticMs = resolvedConfig.cooldowns?.static ?? DEFAULT_COOLDOWN_STATIC_MS;
  const palmHoldDuration = resolvedConfig.palmHoldDuration ?? DEFAULT_PALM_HOLD_DURATION_MS;
  const palmVelocityThreshold =
    resolvedConfig.palmVelocityThreshold ?? DEFAULT_PALM_VELOCITY_THRESHOLD;

  // Store config values in refs for stable callback access.
  const cooldownSwipeMsRef = useRef(cooldownSwipeMs);
  const cooldownStaticMsRef = useRef(cooldownStaticMs);
  const palmHoldDurationRef = useRef(palmHoldDuration);
  const palmVelocityThresholdRef = useRef(palmVelocityThreshold);
  cooldownSwipeMsRef.current = cooldownSwipeMs;
  cooldownStaticMsRef.current = cooldownStaticMs;
  palmHoldDurationRef.current = palmHoldDuration;
  palmVelocityThresholdRef.current = palmVelocityThreshold;

  // --- Pipeline instances (refs so they persist across re-renders) -----------
  const smootherRef = useRef<LandmarkSmoother>(new LandmarkSmoother(smoothingFactor));
  const debouncerRef = useRef<GestureDebouncer>(new GestureDebouncer(debounceFrames));
  const cooldownRef = useRef<GestureCooldown>(new GestureCooldown(cooldownStaticMs));
  const swipeDetectorRef = useRef<SwipeDetector>(new SwipeDetector(300, 0.09));

  // --- Lifecycle refs (init mutex H7, deferred disable V3-C2) ---------------
  const isInitializingRef = useRef(false);
  const pendingDisableRef = useRef(false);
  const isActiveRef = useRef(false);

  // Camera + worker refs.
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const workerRef = useRef<WorkerLike | null>(null);
  const workerReadyRef = useRef(false);

  // rAF loop.
  const rafIdRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef(0);
  const frameIdRef = useRef(0);
  const pendingFrameRef = useRef(false);
  const lastProcessedFrameIdRef = useRef(-1);

  // Orientation listener.
  const orientationHandlerRef = useRef<(() => void) | null>(null);

  // Palm hold tracking (C4).
  const palmHoldStartRef = useRef<number | null>(null);
  const lastLandmarkRef = useRef<Landmark | null>(null);
  const palmHoldFiredRef = useRef(false);

  // --- Worker result handler (pipeline: smooth → debounce → detect → dispatch)
  const handleWorkerResult = useCallback((result: GestureWorkerResult) => {
    // Monotonic frame ordering: drop stale results.
    if (result.frameId <= lastProcessedFrameIdRef.current) return;
    lastProcessedFrameIdRef.current = result.frameId;

    // Release backpressure.
    pendingFrameRef.current = false;

    const { gesture, confidence: conf, landmarks } = result;

    // Log hand presence (landmarks detected = hand is visible to the camera).
    if (landmarks && landmarks.length > 0) {
      console.log(
        `[gesture] 👁️ Hand detected: ${landmarks.length} landmarks | frame: ${result.frameId}${gesture && gesture !== 'None' ? ` | gesture: ${gesture}` : ' | no gesture classified'}`
      );
    }

    // Log raw detection to console for debugging.
    if (gesture && gesture !== 'None') {
      console.log(
        `[gesture] 🖐️ Detected: ${gesture} (${Math.round(conf * 100)}%) | frame: ${result.frameId}`
      );
    }

    // Absent gesture: 'None' or null → no action ever (Req 2.6).
    if (gesture === null || gesture === 'None') {
      setCurrentGesture(null);
      setConfidence(0);
      debouncerRef.current.update(null);
      // Reset palm hold on absence.
      palmHoldStartRef.current = null;
      palmHoldFiredRef.current = false;
      lastLandmarkRef.current = null;
      swipeDetectorRef.current.reset();
      return;
    }

    // Smooth landmarks.
    const smoothedLandmarks = landmarks
      ? smootherRef.current.update(landmarks)
      : null;

    // Update exposed state.
    setCurrentGesture(gesture);
    setConfidence(conf);

    // Debounce: must be confirmed across N consecutive frames.
    const confirmed = debouncerRef.current.update(gesture);
    if (!confirmed) {
      // While accumulating, still feed swipe detector for Open_Palm.
      if (gesture === 'Open_Palm' && smoothedLandmarks && smoothedLandmarks.length > 12) {
        const fingerTip = smoothedLandmarks[12]; // Middle finger tip — more responsive than wrist
        swipeDetectorRef.current.update(fingerTip.x, fingerTip.y);
      } else {
        swipeDetectorRef.current.reset();
        palmHoldStartRef.current = null;
        palmHoldFiredRef.current = false;
        lastLandmarkRef.current = null;
      }
      return;
    }

    // --- Confirmed gesture: determine action --------------------------------
    console.log(`[gesture] ✅ Confirmed: ${gesture} (passed ${debounceFrames}-frame debounce)`);

    // Open_Palm special handling: swipe OR hold (C4).
    if (gesture === 'Open_Palm' && smoothedLandmarks && smoothedLandmarks.length > 12) {
      const fingerTip = smoothedLandmarks[12]; // Middle finger tip for swipe detection
      const wrist = smoothedLandmarks[0]; // Wrist for palm-hold stationarity check

      // Check swipe first (takes priority over hold).
      const swipeDir = swipeDetectorRef.current.update(fingerTip.x, fingerTip.y);
      if (swipeDir) {
        // Swipe detected — dispatch navigation action independent of static config (Property 25).
        if (swipeDir === 'right' && canGoNextRef.current) {
          if (cooldownRef.current.canTrigger('__swipe_next', cooldownSwipeMsRef.current)) {
            cooldownRef.current.trigger('__swipe_next');
            console.log(`[gesture] 🎬 Action dispatched: NEXT (swipe right)`);
            onActionRef.current?.('next');
          }
        } else if (swipeDir === 'left' && canGoPrevRef.current) {
          if (cooldownRef.current.canTrigger('__swipe_prev', cooldownSwipeMsRef.current)) {
            cooldownRef.current.trigger('__swipe_prev');
            console.log(`[gesture] 🎬 Action dispatched: PREV (swipe left)`);
            onActionRef.current?.('prev');
          }
        }
        // Reset palm hold on swipe.
        palmHoldStartRef.current = null;
        palmHoldFiredRef.current = false;
        lastLandmarkRef.current = null;
        return;
      }

      // Palm hold logic: check velocity to determine stationarity.
      const now = performance.now();
      if (lastLandmarkRef.current) {
        const dx = wrist.x - lastLandmarkRef.current.x;
        const dy = wrist.y - lastLandmarkRef.current.y;
        const velocity = Math.sqrt(dx * dx + dy * dy);

        if (velocity > palmVelocityThresholdRef.current) {
          // Moving too fast — reset hold.
          palmHoldStartRef.current = now;
          palmHoldFiredRef.current = false;
        } else {
          // Stationary.
          if (palmHoldStartRef.current === null) {
            palmHoldStartRef.current = now;
          }
          // Check if hold duration met.
          if (
            !palmHoldFiredRef.current &&
            now - palmHoldStartRef.current >= palmHoldDurationRef.current
          ) {
            palmHoldFiredRef.current = true;
            if (cooldownRef.current.canTrigger('__palm_hold', cooldownStaticMsRef.current)) {
              cooldownRef.current.trigger('__palm_hold');
              console.log(`[gesture] 🎬 Action dispatched: TOGGLE_UI (palm hold 1s)`);
              onActionRef.current?.('toggleUI');
            }
          }
        }
      } else {
        // First confirmed Open_Palm frame — start hold timer.
        palmHoldStartRef.current = now;
      }
      lastLandmarkRef.current = wrist;
      return;
    }

    // Non Open_Palm confirmed gesture: static dispatch via config (Property 22).
    swipeDetectorRef.current.reset();
    palmHoldStartRef.current = null;
    palmHoldFiredRef.current = false;
    lastLandmarkRef.current = null;

    const action = actionByGestureRef.current.get(gesture);
    if (!action) return;

    // Boundary guard for static config that maps to next/prev (H2).
    if (action === 'next' && !canGoNextRef.current) return;
    if (action === 'prev' && !canGoPrevRef.current) return;

    if (cooldownRef.current.canTrigger(gesture, cooldownStaticMsRef.current)) {
      cooldownRef.current.trigger(gesture);
      console.log(`[gesture] 🎬 Action dispatched: ${action.toUpperCase()} (${gesture})`);
      onActionRef.current?.(action);
    }
  }, []);

  // --- Processing loop (rAF @ 30fps) ----------------------------------------
  const frameIntervalRef = useRef(1000 / processingFps);
  frameIntervalRef.current = 1000 / processingFps;

  const processLoop = useCallback((_timestamp: number) => {
    // Guard: if no longer active (disable was called), do NOT re-arm.
    if (!isActiveRef.current) return;

    const video = videoRef.current;
    if (!video || !workerReadyRef.current) {
      // Video element not created or worker not ready yet; re-arm.
      if (frameIdRef.current === 0 && video) {
        // Only log once to avoid flooding.
        console.log(`[gesture-worker] ⏳ Waiting for worker ready... (workerReady=${workerReadyRef.current})`);
      }
      rafIdRef.current = requestAnimationFrame(processLoop);
      return;
    }

    const now = performance.now();
    if (now - lastFrameTimeRef.current < frameIntervalRef.current) {
      rafIdRef.current = requestAnimationFrame(processLoop);
      return;
    }
    lastFrameTimeRef.current = now;

    // Backpressure: only one frame in-flight at a time.
    if (!pendingFrameRef.current) {
      pendingFrameRef.current = true;
      const currentFrameId = frameIdRef.current++;

      if (currentFrameId < 3) {
        console.log(`[gesture-worker] 📷 Sending frame ${currentFrameId} to worker for processing`);
      }

      createImageBitmap(video)
        .then((bitmap) => {
          if (!isActiveRef.current) {
            bitmap.close();
            return;
          }
          workerRef.current?.postMessage(
            { type: 'process', imageBitmap: bitmap, timestamp: performance.now(), frameId: currentFrameId },
            [bitmap]
          );
        })
        .catch((err) => {
          console.error(`[gesture-worker] ❌ createImageBitmap failed:`, err);
          // Decode failure — release backpressure.
          pendingFrameRef.current = false;
        });
    }

    rafIdRef.current = requestAnimationFrame(processLoop);
  }, []);

  // --- performDisable: synchronous teardown ---------------------------------
  const performDisable = useCallback(() => {
    isActiveRef.current = false;

    // Cancel rAF loop.
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    // Stop all camera tracks.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Clear video srcObject.
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    // Terminate worker.
    if (workerRef.current) {
      workerRef.current.onmessage = null;
      workerRef.current.onerror = null;
      workerRef.current.terminate();
      workerRef.current = null;
    }
    workerReadyRef.current = false;

    // Remove orientation listener.
    if (orientationHandlerRef.current) {
      window.removeEventListener('orientationchange', orientationHandlerRef.current);
      orientationHandlerRef.current = null;
    }

    // Reset pipeline state.
    smootherRef.current.reset();
    debouncerRef.current.reset();
    cooldownRef.current.reset();
    swipeDetectorRef.current.reset();
    palmHoldStartRef.current = null;
    palmHoldFiredRef.current = false;
    lastLandmarkRef.current = null;
    lastFrameTimeRef.current = 0;
    frameIdRef.current = 0;
    lastProcessedFrameIdRef.current = -1;
    pendingFrameRef.current = false;

    // Reset hook state.
    setIsActive(false);
    setCurrentGesture(null);
    setConfidence(0);
    setError(null);
  }, []);

  // --- enable() -------------------------------------------------------------
  const enable = useCallback(async () => {
    // Init mutex (H7): if already initializing or already active, no-op.
    if (isInitializingRef.current || isActiveRef.current) return;
    isInitializingRef.current = true;
    pendingDisableRef.current = false;

    setIsLoading(true);
    setError(null);

    try {
      // Request camera (facingMode 'user').
      const mediaStream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);

      // Store stream regardless of pending disable — performDisable will clean up.
      streamRef.current = mediaStream;

      // Create/reuse video element.
      if (!videoRef.current) {
        const video = document.createElement('video');
        video.autoplay = true;
        video.playsInline = true;
        video.muted = true;
        videoRef.current = video;
      }
      videoRef.current.srcObject = mediaStream;

      // Create worker via injection point.
      const factory = createWorkerRef.current ?? defaultCreateWorker;
      const worker = factory();
      workerRef.current = worker;
      workerReadyRef.current = false;

      // Wire worker message handler.
      worker.onmessage = (e: { data: GestureWorkerResponse }) => {
        const data = e.data;
        console.log(`[gesture-worker] 📨 Message from worker:`, data.type, data.type === 'error' ? (data as { message?: string }).message : '');
        if (data.type === 'ready') {
          console.log(`[gesture-worker] ✅ Worker READY — will begin processing frames`);
          workerReadyRef.current = true;
          return;
        }
        if (data.type === 'result') {
          handleWorkerResult(data);
          return;
        }
        if (data.type === 'error') {
          console.error(`[gesture-worker] ❌ Worker error:`, (data as { message?: string }).message);
          setError((data as { message?: string }).message ?? 'Unknown worker error');
        }
      };
      worker.onerror = (err) => {
        console.error(`[gesture-worker] 💥 Worker crashed (onerror):`, err);
        setError('Gesture recognition worker crashed.');
      };

      // Send init message to worker.
      console.log(`[gesture-worker] 🚀 Sending init message to worker...`);
      worker.postMessage({
        type: 'init',
        config: {
          minHandDetectionConfidence: 0.5,
          minHandPresenceConfidence: 0.5,
          minTrackingConfidence: 0.5,
          scoreThreshold: 0.5,
        },
      });

      // Post-await disposed re-check (V4-4/M-1): if disable() was queued
      // while we awaited the camera, skip activation — the finally block
      // will run performDisable() which cleans everything up.
      if (pendingDisableRef.current) {
        return;
      }

      // Start the rAF processing loop. The loop internally gates on
      // workerReadyRef so frames only flow after the worker signals ready.
      lastFrameTimeRef.current = 0;
      isActiveRef.current = true;
      rafIdRef.current = requestAnimationFrame(processLoop);

      // Add orientationchange listener (H5).
      const orientationHandler = () => {
        swipeDetectorRef.current.reset();
      };
      orientationHandlerRef.current = orientationHandler;
      window.addEventListener('orientationchange', orientationHandler);

      // Session is active.
      setIsActive(true);
    } catch {
      setError('Camera permission denied. Hands-free mode requires camera access.');
      setIsActive(false);
      isActiveRef.current = false;
    } finally {
      setIsLoading(false);
      isInitializingRef.current = false;

      // Deferred disable (V3-C2): if disable() was called during init, run it now.
      if (pendingDisableRef.current) {
        pendingDisableRef.current = false;
        performDisable();
      }
    }
  }, [processLoop, performDisable, handleWorkerResult]);

  // --- disable() ------------------------------------------------------------
  const disable = useCallback(() => {
    if (isInitializingRef.current) {
      // Init in progress: defer teardown (V3-C2).
      pendingDisableRef.current = true;
      return;
    }
    performDisable();
  }, [performDisable]);

  // --- Unmount cleanup (equivalent to disable) ------------------------------
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (isActiveRef.current) {
        performDisable();
      }
    };
  }, [performDisable]);

  return {
    isActive,
    isLoading,
    currentGesture,
    confidence,
    error,
    stream: streamRef.current,
    enable,
    disable,
  };
}
